import {
  IsString,
  IsNumber,
  IsIn,
  IsBoolean,
  IsOptional,
  IsDefined,
  ValidateNested,
  Min,
  Max,
} from 'class-validator';
import { Type } from 'class-transformer';
import { IsColorOrReference } from '@builder4/shared/dto/styles/validators/IsColorOrReference';
import { IsRgbaOrReference } from '@builder4/shared/dto/styles/validators/IsRgbaOrReference';
import {
  VisibilityStateDto,
  PositionAnchorsStateDto,
  ShadowSettingsDto,
  SHADOW_TYPE_VALUES,
  type ShadowType,
} from '@builder4/shared/dto/craft/props/shared';
import { IsTRBLNumberCoords } from '@builder4/shared/dto/craft/validators/IsTRBLNumberCoords';
import { IsVideoUrl } from '@builder4/shared/dto/craft/validators/IsVideoUrl';
import { IsVideoEmbedCode } from '@builder4/shared/dto/craft/validators/IsVideoEmbedCode';
import { GradientDto, RgbaDto } from '@builder4/shared/dto/styles';
import {
  ANIMATION_TYPES,
  ANIMATION_EASINGS,
  HOVER_EFFECT_TYPES,
  IMAGE_CONTINUOUS_TYPES,
} from '@builder4/shared/types/animation';

// Constants for validation
const FLEX_DIRECTION_VALUES = ['row', 'column'] as const;
const ALIGN_ITEMS_VALUES = ['flex-start', 'center', 'flex-end'] as const;
const JUSTIFY_CONTENT_VALUES = ['flex-start', 'flex-end', 'center'] as const;
const OVERFLOW_VALUES = ['visible', 'hidden', 'scroll', 'auto'] as const;
const POSITION_VALUES = ['relative', 'absolute', 'fixed', 'sticky'] as const;
const FILL_SPACE_VALUES = ['yes', 'no'] as const;
const MAX_WIDTH_UNIT_VALUES = ['px', '%'] as const;
const BACKGROUND_IMAGE_MODES = ['fill', 'fit', 'stretch', 'tile'] as const;
const BACKGROUND_POSITIONS = [
  'left top',
  'center top',
  'right top',
  'left center',
  'center center',
  'right center',
  'left bottom',
  'center bottom',
  'right bottom',
] as const;
const VIDEO_SOURCE_TYPES = ['url', 'embed'] as const;
const VIDEO_PROVIDERS = ['youtube', 'vimeo', 'wistia', 'custom'] as const;
const VIDEO_FIT_TYPES = ['fill', 'fit'] as const;
const SHAPE_DIVIDER_TYPES = ['wave', 'zigzag', 'curve', 'bowl', 'tilt'] as const;
const SHAPE_DIVIDER_POSITIONS = ['top', 'bottom'] as const;

/**
 * Breakpoint-aware properties for Container element.
 * Based on getDetachablePropsForNode('Content Block'):
 * - From DETACHABLE_PROPS_REGISTRY: flexDirection, width, height, fillSpace, flexBasis, gap, justifyContent, alignItems, disableMaxWidth, shapeDividerHeight
 * - BOUNDING_BOX_PROPS: padding, margin, borderRadius
 * - POSITION_PROPS: position, positionAnchorsState, left, right, top, bottom, zIndex
 * - MAX_WIDTH_PROPS: maxWidth, maxWidthUnit
 */
export class ContainerBreakpointPropsDto {
  // From DETACHABLE_PROPS_REGISTRY[CONTENT_BLOCK_NAME]
  @IsOptional()
  @IsIn([...FLEX_DIRECTION_VALUES])
  flexDirection?: (typeof FLEX_DIRECTION_VALUES)[number];

  @IsOptional()
  @IsString()
  width?: string;

  @IsOptional()
  @IsString()
  height?: string;

  @IsOptional()
  @IsIn([...FILL_SPACE_VALUES])
  fillSpace?: (typeof FILL_SPACE_VALUES)[number];

  @IsOptional()
  @IsString()
  flexBasis?: string;

  @IsOptional()
  @IsNumber()
  @Min(0)
  gap?: number;

  @IsOptional()
  @IsIn([...JUSTIFY_CONTENT_VALUES])
  justifyContent?: (typeof JUSTIFY_CONTENT_VALUES)[number];

  @IsOptional()
  @IsIn([...ALIGN_ITEMS_VALUES])
  alignItems?: (typeof ALIGN_ITEMS_VALUES)[number];

  @IsOptional()
  @IsBoolean()
  disableMaxWidth?: boolean;

  @IsOptional()
  @IsNumber()
  @Min(0)
  shapeDividerHeight?: number;

  // WHY: borderWidth is used by Container's breakpoint rendering logic for inner border-radius
  // calculation (tablet.borderWidth / mobile.borderWidth) even though it is not in DETACHABLE_PROPS_REGISTRY.
  @IsOptional()
  @IsNumber()
  @Min(0)
  borderWidth?: number;

  // BOUNDING_BOX_PROPS
  @IsOptional()
  @IsTRBLNumberCoords()
  padding?: [number, number, number, number];

  @IsOptional()
  @IsTRBLNumberCoords()
  margin?: [number, number, number, number];

  @IsOptional()
  @IsTRBLNumberCoords()
  borderRadius?: [number, number, number, number];

  // POSITION_PROPS
  @IsOptional()
  @IsIn([...POSITION_VALUES])
  position?: (typeof POSITION_VALUES)[number];

  @IsOptional()
  @ValidateNested()
  @Type(() => PositionAnchorsStateDto)
  positionAnchorsState?: PositionAnchorsStateDto;

  @IsOptional()
  @IsString()
  left?: string;

  @IsOptional()
  @IsString()
  right?: string;

  @IsOptional()
  @IsString()
  top?: string;

  @IsOptional()
  @IsString()
  bottom?: string;

  @IsOptional()
  @IsNumber()
  @Min(-1)
  @Max(100)
  zIndex?: number;

  // MAX_WIDTH_PROPS
  @IsOptional()
  @IsNumber()
  @Min(0)
  maxWidth?: number;

  @IsOptional()
  @IsIn([...MAX_WIDTH_UNIT_VALUES])
  maxWidthUnit?: (typeof MAX_WIDTH_UNIT_VALUES)[number];
}

export class ContainerPropsDto {
  // Layout styles
  @IsDefined()
  @IsIn([...FLEX_DIRECTION_VALUES])
  flexDirection!: (typeof FLEX_DIRECTION_VALUES)[number];

  @IsDefined()
  @IsIn([...ALIGN_ITEMS_VALUES])
  alignItems!: (typeof ALIGN_ITEMS_VALUES)[number];

  @IsDefined()
  @IsIn([...JUSTIFY_CONTENT_VALUES])
  justifyContent!: (typeof JUSTIFY_CONTENT_VALUES)[number];

  @IsDefined()
  @IsNumber()
  @Min(0)
  gap!: number;

  // Sizing
  @IsDefined()
  @IsString()
  width!: string;

  @IsDefined()
  @IsString()
  height!: string;

  @IsDefined()
  @IsNumber()
  @Min(0)
  minHeight!: number;

  @IsDefined()
  @IsNumber()
  @Min(0)
  minWidth!: number;

  @IsDefined()
  @IsNumber()
  @Min(0)
  maxWidth!: number;

  @IsDefined()
  @IsIn([...MAX_WIDTH_UNIT_VALUES])
  maxWidthUnit!: (typeof MAX_WIDTH_UNIT_VALUES)[number];

  // Spacing
  @IsDefined()
  @IsTRBLNumberCoords()
  padding!: [number, number, number, number];

  @IsDefined()
  @IsTRBLNumberCoords()
  margin!: [number, number, number, number];

  // Colors
  @IsOptional()
  @IsColorOrReference()
  backgroundColor?: RgbaDto | GradientDto | string;

  @IsOptional()
  @ValidateNested()
  @Type(() => GradientDto)
  bgGradient?: GradientDto;

  // WHY: Container intentionally uses @IsRgbaOrReference() (allows color references like '$ref:#/colors/…')
  // rather than @ValidateNested() RgbaDto, unlike PagePropsDto which restricts bgSolid to plain RGBA only.
  @IsOptional()
  @IsRgbaOrReference()
  bgSolid?: RgbaDto | string;

  @IsOptional()
  @IsRgbaOrReference()
  borderColor?: RgbaDto | string;

  @IsOptional()
  @IsRgbaOrReference()
  hoverBorderColor?: RgbaDto | string;

  // Borders
  @IsDefined()
  @IsNumber()
  @Min(0)
  borderWidth!: number;

  @IsDefined()
  @IsTRBLNumberCoords()
  borderRadius!: [number, number, number, number];

  // Background image
  @IsOptional()
  @IsString()
  bgImage?: string | null;

  @IsDefined()
  @IsNumber()
  @Min(1)
  @Max(500)
  bgImageScale!: number;

  @IsDefined()
  @IsNumber()
  @Min(0)
  @Max(100)
  bgImageOpacity!: number;

  @IsDefined()
  @IsIn([...BACKGROUND_IMAGE_MODES])
  bgImageMode!: (typeof BACKGROUND_IMAGE_MODES)[number];

  @IsDefined()
  @IsIn([...BACKGROUND_POSITIONS])
  bgImagePosition!: (typeof BACKGROUND_POSITIONS)[number];

  // Background video
  @IsDefined()
  @IsIn([...VIDEO_SOURCE_TYPES])
  bgVideoSourceType!: (typeof VIDEO_SOURCE_TYPES)[number];

  @IsOptional()
  @IsVideoUrl()
  bgVideoUrl?: string | null;

  @IsOptional()
  @IsIn([...VIDEO_PROVIDERS])
  bgVideoProvider?: (typeof VIDEO_PROVIDERS)[number];

  @IsOptional()
  @IsString()
  bgVideoThumbnailUrl?: string | null;

  @IsOptional()
  @IsVideoEmbedCode()
  bgVideoEmbedCode?: string | null;

  @IsDefined()
  @IsIn([...VIDEO_FIT_TYPES])
  bgVideoMode!: (typeof VIDEO_FIT_TYPES)[number];

  @IsDefined()
  @IsNumber()
  @Min(0)
  @Max(100)
  bgVideoOpacity!: number;

  // Other styles
  @IsDefined()
  @IsNumber()
  @Min(0)
  shadow!: number;

  @IsOptional()
  @IsIn([...SHADOW_TYPE_VALUES])
  shadowType?: ShadowType;

  @IsOptional()
  @ValidateNested()
  @Type(() => ShadowSettingsDto)
  shadowSettings?: ShadowSettingsDto;

  @IsDefined()
  @IsNumber()
  @Min(0)
  @Max(1)
  opacity!: number;

  @IsDefined()
  @IsIn([...OVERFLOW_VALUES])
  overflow!: (typeof OVERFLOW_VALUES)[number];

  @IsDefined()
  @IsNumber()
  @Min(-1)
  @Max(100)
  zIndex!: number;

  // Position
  @IsDefined()
  @IsIn([...POSITION_VALUES])
  position!: (typeof POSITION_VALUES)[number];

  @IsOptional()
  @IsString()
  top?: string;

  @IsOptional()
  @IsString()
  left?: string;

  @IsOptional()
  @IsString()
  right?: string;

  @IsOptional()
  @IsString()
  bottom?: string;

  @IsDefined()
  @IsIn([...FILL_SPACE_VALUES])
  fillSpace!: (typeof FILL_SPACE_VALUES)[number];

  @IsDefined()
  @IsString()
  flexBasis!: string;

  @IsDefined()
  @IsNumber()
  @Min(0)
  flexGrow!: number;

  @IsDefined()
  @IsNumber()
  @Min(0)
  placeholderHeight!: number;

  @IsDefined()
  @ValidateNested()
  @Type(() => PositionAnchorsStateDto)
  positionAnchorsState!: PositionAnchorsStateDto;

  @IsDefined()
  @ValidateNested()
  @Type(() => VisibilityStateDto)
  visibility!: VisibilityStateDto;

  // Marking as optional for now.
  // Marking as required is blowing up all saved sections
  @IsOptional()
  @IsBoolean()
  disableMaxWidth?: boolean;

  // Aspect ratio
  @IsDefined()
  @IsBoolean()
  aspectRatioLock!: boolean;

  @IsOptional()
  @IsNumber()
  @Min(0)
  aspectRatio?: number;

  // Shape divider
  @IsOptional()
  @IsIn(['none', ...SHAPE_DIVIDER_TYPES])
  shapeDividerType?: 'none' | (typeof SHAPE_DIVIDER_TYPES)[number];

  @IsOptional()
  @IsIn([...SHAPE_DIVIDER_POSITIONS])
  shapeDividerPosition?: (typeof SHAPE_DIVIDER_POSITIONS)[number];

  @IsOptional()
  @IsNumber()
  @Min(0)
  shapeDividerHeight?: number;

  @IsOptional()
  @IsColorOrReference()
  shapeDividerColor?: RgbaDto | GradientDto | string;

  @IsOptional()
  @IsNumber()
  @Min(1)
  @Max(20)
  shapeDividerFrequency?: number;

  @IsOptional()
  @IsBoolean()
  shapeDividerFlip?: boolean;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(100)
  shapeDividerCurveCenter?: number;

  // Animation
  @IsOptional()
  @IsIn([...ANIMATION_TYPES])
  animationType?: (typeof ANIMATION_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(100)
  @Max(2000)
  animationDuration?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(2000)
  animationDelay?: number;

  @IsOptional()
  @IsIn([...ANIMATION_EASINGS])
  animationEasing?: (typeof ANIMATION_EASINGS)[number];

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(2000)
  staggerDelay?: number;

  // Hover effect
  @IsOptional()
  @IsIn([...HOVER_EFFECT_TYPES])
  hoverEffect?: (typeof HOVER_EFFECT_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(2000)
  hoverTransitionDuration?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(200)
  hoverIntensity?: number;

  // Background image continuous animation
  @IsOptional()
  @IsIn([...IMAGE_CONTINUOUS_TYPES])
  bgContinuousAnimation?: (typeof IMAGE_CONTINUOUS_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(1)
  @Max(30)
  bgContinuousSpeed?: number;

  @IsOptional()
  @IsNumber()
  @Min(1)
  @Max(50)
  bgContinuousIntensity?: number;

  // Breakpoint overrides (optional, partial props)
  @IsOptional()
  @ValidateNested()
  @Type(() => ContainerBreakpointPropsDto)
  tablet?: ContainerBreakpointPropsDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => ContainerBreakpointPropsDto)
  mobile?: ContainerBreakpointPropsDto;
}
